<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('申報開銷') }}
      </oxd-text>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <claim-event-dropdown
                v-model="request.event"
                :rules="rules.event"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="request.currency"
                type="select"
                :rules="rules.currency"
                :options="currencies"
                :label="$t('general.currency')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <claim-expense-type-dropdown
                v-model="expense.type"
                label="開銷類型"
                :rules="rules.type"
              ></claim-expense-type-dropdown>
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="expense.date"
                :label="$t('general.date')"
                :rules="rules.date"
                :years="yearsArray"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="expense.amount"
                label="銀碼"
                :rules="rules.amount"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="attachment.attachment"
                type="file"
                :label="$t('general.select_file')"
                :button-label="$t('general.browse')"
                :rules="rules.attachment"
                :placeholder="$t('general.no_file_selected')"
                required
                @change="onFileSelect"
              />
              <oxd-text class="orangehrm-input-hint" tag="p">
                {{ $t('general.accepts_up_to_1mb') }}
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item class="--span-column-2">
              <oxd-input-field
                v-model="request.remarks"
                type="textarea"
                :label="$t('備註')"
                :rules="rules.remarks"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button :label="$t('新增')" />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {watch} from 'vue';
import {
  required,
  shouldNotExceedCharLength,
  validDateFormat,
  maxCurrency,
  digitsOnlyWithTwoDecimalPoints,
  maxFileSize,
  validFileTypes,
} from '@/core/util/validation/rules';
import {APIService} from '@ohrm/core/util/services/api.service';
import {navigate} from '@ohrm/core/util/helper/navigation';
import ClaimEventDropdownVue from '../../components/ClaimEventDropdown.vue';
import ClaimExpenseTypeDropdown from '../../components/ClaimExpenseTypeDropdown.vue';
import {yearRange} from '@ohrm/core/util/helper/year-range';

const claimRequest = {
  event: null,
  currency: null,
  remarks: null,
};

const attachmentModel = {
  attachment: null,
  description: '',
};

const expenseModel = {
  type: null,
  date: null,
  amount: null,
  note: '',
};

export default {
  name: 'SubmitClaimRequest',

  components: {
    'claim-event-dropdown': ClaimEventDropdownVue,
    'claim-expense-type-dropdown': ClaimExpenseTypeDropdown,
  },

  props: {
    currencies: {
      type: Array,
      default: () => [],
    },
    allowedFileTypes: {
      type: Array,
      required: true,
    },
    maxFileSize: {
      type: Number,
      required: true,
      default: 20000000,
    },
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/claim/requests',
    );

    return {
      http,
    };
  },

  data() {
    const today = new Date();
    const formattedDate =
      today.getFullYear() +
      '-' +
      (today.getMonth() + 1).toString().padStart(2, '0') +
      '-' +
      today.getDate().toString().padStart(2, '0');
    return {
      isLoading: false,
      yearsArray: [...yearRange()],
      request: {
        ...claimRequest,
        currency:
          this.currencies.find((currency) => currency.id === 'HKD') || null,
      },
      attachment: {
        ...attachmentModel,
      },
      expense: {
        ...expenseModel,
        date: formattedDate,
      },
      id: 0,
      rules: {
        type: [required],
        event: [required],
        currency: [required],
        remarks: [shouldNotExceedCharLength(1000)],
        date: [required, validDateFormat(this.userDateFormat)],
        amount: [
          required,
          maxCurrency(10000000000),
          digitsOnlyWithTwoDecimalPoints,
        ],
        attachment: [required],
      },
    };
  },

  methods: {
    onFileSelect(event) {
      console.log('onFileSelect', event.target.files[0]);
      const file = event.target.files[0];
      if (!file) return;

      const maxSize = 1024;
      const quality = 0.9;

      // 檢查文件類型
      const validImageTypes = ['image/jpeg', 'image/png'];
      if (!validImageTypes.includes(file.type)) {
        this.$toast.error('請選擇有效的圖像文件（JPEG 或 PNG）');
        return;
      }

      const base64Size = (base64String) => {
        const padding = (base64String.match(/=+$/) || []).length;
        return (base64String.length * 3) / 4 - padding;
      };

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 800; // 調整到的寬度
          const scaleSize = maxWidth / img.width;
          canvas.width = maxWidth;
          canvas.height = img.height * scaleSize;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // 轉換為 base64 字符串
          const resizedImage = canvas
            .toDataURL(file.type, quality)
            .split(',')[1];

          // 將調整後的圖像存儲到模型中
          this.attachment.attachment.base64 = resizedImage;
          this.attachment.attachment.size = base64Size(resizedImage);
          this.attachment.attachment.name = file.name;
          this.attachment.attachment.type = file.type;
        };
      };
      reader.readAsDataURL(file);
    },
    onCancel() {
      console.log('onCancel', this.attachment.attachment);
      // navigate('/claim/viewClaim');
    },
    onSave() {
      this.isLoading = true;
      this.http
        .create({
          claimEventId: this.request.event.id,
          currencyId: this.request.currency.id,
          remarks: this.request.remarks,
        })
        .then((res) => {
          this.id = res.data.data.id;
          const httpB = new APIService(
            window.appGlobal.baseUrl,
            `/api/v2/claim/requests/${this.id}/expenses`,
          );

          httpB
            .create({
              expenseTypeId: this.expense.type.id,
              date: this.expense.date,
              amount: Number(this.expense.amount).toFixed(2),
              note: this.expense.note,
            })
            .then(() => {
              const httpC = new APIService(
                window.appGlobal.baseUrl,
                `/api/v2/claim/requests/${this.id}/attachments`,
              );
              httpC
                .create({
                  ...this.attachment,
                })
                .then(() => {
                  return this.$toast.saveSuccess();
                })
                .then(() => {
                  navigate('/claim/submitClaim/id/{id}', {id: this.id});
                });
            });
        });
    },
  },
};
</script>
